import React, { useEffect, useId, useState, useMemo } from 'react';
import { Modal } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import menuClose from '../../../assets/images/exit.svg';
import { usePopup } from './PopupContext';

export interface PopupProps extends Omit<ModalProps, 'visible'> {
  visible?: boolean;
  children: React.ReactNode;
  className?: string;
  popupId?: string;
}

export const Popup: React.FC<PopupProps> = ({
  visible = false,
  children,
  className = '',
  footer = null,
  closable = true,
  maskClosable = true,
  centered = true,
  style,
  title,
  closeIcon,
  popupId: externalId,
  width = 320, // Default standard width for consistency
  ...rest
}) => {
  // Generate a unique ID for this popup instance if not provided externally
  const generatedId = useId();
  const popupId = externalId || generatedId;
  
  // Use the popup context to get z-index and manage stacking
  const { registerPopup, unregisterPopup, updatePopupVisibility, getZIndex, getStackIndex } = usePopup();
  
  // Initialize this popup in the popup registry when mounted
  const [zIndex, setZIndex] = useState(0);
  const [stackIndex, setStackIndex] = useState(0);
  
  // Register this popup when component mounts
  useEffect(() => {
    const { zIndex: newZIndex, stackIndex: newStackIndex } = registerPopup(popupId);
    setZIndex(newZIndex);
    setStackIndex(newStackIndex);
    
    // Clean up on unmount
    return () => {
      unregisterPopup(popupId);
    };
  }, [popupId, registerPopup, unregisterPopup]);
  
  // Update visibility in the popup manager
  useEffect(() => {
    updatePopupVisibility(popupId, visible);
  }, [popupId, visible, updatePopupVisibility]);
  
  // Calculate mask opacity based on stack position
  // The higher in the stack, the darker the mask
  const getMaskBackgroundColor = useMemo(() => {
    const baseOpacity = 0.5;
    const opacityIncrement = 0.1;
    const maxOpacity = 0.85;
    
    const opacity = Math.min(
      baseOpacity + (stackIndex * opacityIncrement),
      maxOpacity
    );
    
    return `rgba(4, 7, 18, ${opacity})`;
  }, [stackIndex]);
  return (
    <Modal
      open={visible}
      closable={closable}
      maskClosable={maskClosable}
      centered={centered}
      className={`custom-popup rounded-xl px-1 ${className}`}
      style={{ padding: 0, ...style }}
      closeIcon={closeIcon || <img src={menuClose} className="popup-close-icon" alt="Close" />}
      footer={footer}
      title={title ? <div className="text-center w-full">{title}</div> : null}
      // Apply dynamic z-index and background color styles
      styles={{
        mask: { 
          zIndex: zIndex - 20,
          backgroundColor: getMaskBackgroundColor 
        },     // Lower zIndex for mask
        wrapper: { zIndex: zIndex - 10 },  // Slightly higher for wrapper
        content: { zIndex }                // Highest for actual content
      }}
      modalRender={(node) => {
        // Add custom class to ensure proper z-index
        return <div 
          className="vf-modal-wrapper"
          data-popup-id={popupId}
          data-stack-index={stackIndex}
        >{node}</div>;
      }}
      {...rest}
    >
      {children}
    </Modal>
  );
};
